import React, { useState } from 'react';
import { useTopAppProviders } from '../../hooks/useTopAppProviders';
import { truncateParty, formatNumber } from '../../utils/formatters';
import { LoadingLogo } from '../LoadingLogo';

export function AppsTab() {
  const { apps, asOfRound, loading, error } = useTopAppProviders(1000);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFeatured, setFilterFeatured] = useState(false);

  // Filter apps by search term and featured status
  const filteredApps = apps.filter(app => {
    const matchesSearch =
      app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.provider.toLowerCase().includes(searchTerm.toLowerCase());

    if (filterFeatured) {
      return matchesSearch && app.isFeatured;
    }

    return matchesSearch;
  });

  const featuredCount = apps.filter(a => a.isFeatured).length;

  return (
    <section style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border-default)',
      borderRadius: '8px',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 24px',
        borderBottom: '1px solid var(--border-default)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            marginBottom: '4px',
          }}>Top App Providers by Rewards</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
            App providers ranked by total rewards earned {asOfRound ? `(as of round #${asOfRound.toLocaleString()})` : ''}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{
            fontSize: '13px',
            color: 'var(--text-secondary)',
            background: 'var(--bg-subtle)',
            padding: '8px 16px',
            borderRadius: '20px',
            fontWeight: '600',
            border: '1px solid var(--border-default)',
          }}>
            {apps.length} Providers
          </span>
          <span style={{
            fontSize: '13px',
            color: 'var(--text-secondary)',
            background: 'var(--bg-subtle)',
            padding: '8px 16px',
            borderRadius: '20px',
            fontWeight: '600',
            border: '1px solid var(--border-default)',
          }}>
            {featuredCount} Featured
          </span>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        padding: '12px',
        borderBottom: '1px solid var(--border-subtle)',
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        {/* Featured Filter Toggle */}
        <button
          onClick={() => setFilterFeatured(!filterFeatured)}
          style={{
            background: filterFeatured ? 'rgba(255, 255, 255, 0.08)' : 'var(--bg-subtle)',
            border: filterFeatured ? '1px solid var(--border-strong)' : '1px solid var(--border-default)',
            borderRadius: '4px',
            padding: '8px 10px',
            fontSize: '11px',
            color: filterFeatured ? 'var(--text-primary)' : 'var(--text-tertiary)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            flexShrink: 0,
          }}
        >
          <span style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: filterFeatured ? 'var(--text-primary)' : 'var(--text-disabled)',
          }} />
          Featured
        </button>

        {/* Search */}
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            background: 'var(--bg-subtle)',
            border: '1px solid var(--border-default)',
            borderRadius: '4px',
            padding: '8px 10px',
            fontSize: '11px',
            color: 'var(--text-primary)',
            outline: 'none',
            flex: 1,
            minWidth: '100px',
            maxWidth: '200px',
          }}
        />
      </div>

      {/* Desktop Column Headers */}
      <div className="hide-mobile" style={{
        display: 'grid',
        gridTemplateColumns: '60px minmax(0, 1.5fr) minmax(0, 1fr) 140px 100px',
        gap: '16px',
        padding: '12px 24px',
        background: 'var(--bg-subtle)',
        fontSize: '11px',
        color: 'var(--text-disabled)',
        textTransform: 'uppercase',
        letterSpacing: '1px',
      }}>
        <div>Rank</div>
        <div>App Provider</div>
        <div>Party ID</div>
        <div style={{ textAlign: 'right' }}>Rewards</div>
        <div style={{ textAlign: 'right' }}>Status</div>
      </div>
      {/* Mobile Column Headers */}
      <div className="show-mobile-only" style={{
        display: 'flex',
        padding: '10px 12px',
        background: 'var(--bg-subtle)',
        fontSize: '9px',
        color: 'var(--text-disabled)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        alignItems: 'center',
      }}>
        <div style={{ width: '35px', flexShrink: 0 }}>Rank</div>
        <div style={{ flex: 1 }}>Provider</div>
        <div style={{ width: '70px', textAlign: 'right', flexShrink: 0 }}>Rewards</div>
        <div style={{ width: '50px', textAlign: 'right', flexShrink: 0 }}>Status</div>
      </div>

      {/* App List */}
      <div style={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
        {loading ? (
          <LoadingLogo size={40} />
        ) : error ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--negative)' }}>
            Error: {error}
          </div>
        ) : filteredApps.length > 0 ? (
          filteredApps.map((app, i) => (
            <div key={app.provider}>
              {/* Desktop row */}
              <div
                className="hide-mobile"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '60px minmax(0, 1.5fr) minmax(0, 1fr) 140px 100px',
                  gap: '16px',
                  padding: '14px 24px',
                  borderBottom: '1px solid var(--border-subtle)',
                  alignItems: 'center',
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{
                  fontSize: '14px',
                  color: i < 3 ? 'var(--text-primary)' : 'var(--text-tertiary)',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: i < 3 ? '700' : '400',
                }}>#{i + 1}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {app.name}
                  </div>
                </div>
                <div style={{
                  color: 'var(--text-disabled)',
                  fontSize: '12px',
                  fontFamily: 'var(--font-mono)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {truncateParty(app.provider)}
                </div>
                <div style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  textAlign: 'right',
                  fontWeight: '600',
                }}>
                  {formatNumber(app.rewards)} CC
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  justifyContent: 'flex-end',
                }}>
                  {app.isFeatured ? (
                    <>
                      <span style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: 'var(--text-primary)',
                      }} />
                      <span style={{
                        fontSize: '11px',
                        color: 'var(--text-tertiary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>FEATURED</span>
                    </>
                  ) : (
                    <span style={{ fontSize: '12px', color: 'var(--text-disabled)' }}>—</span>
                  )}
                </div>
              </div>
              {/* Mobile row */}
              <div
                className="show-mobile-only"
                style={{
                  display: 'flex',
                  padding: '12px',
                  borderBottom: '1px solid var(--border-subtle)',
                  alignItems: 'center',
                }}
              >
                <div style={{
                  width: '35px',
                  flexShrink: 0,
                  fontSize: '12px',
                  color: i < 3 ? 'var(--text-primary)' : 'var(--text-tertiary)',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: i < 3 ? '700' : '400',
                }}>#{i + 1}</div>
                <div style={{
                  flex: 1,
                  minWidth: 0,
                }}>
                  <div style={{
                    color: 'var(--text-primary)',
                    fontSize: '11px',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {app.name}
                  </div>
                </div>
                <div style={{
                  width: '70px',
                  flexShrink: 0,
                  fontFamily: 'var(--font-mono)',
                  fontSize: '10px',
                  color: 'var(--text-secondary)',
                  textAlign: 'right',
                  fontWeight: '600',
                }}>
                  {formatNumber(app.rewards)}
                </div>
                <div style={{
                  width: '50px',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  justifyContent: 'flex-end',
                }}>
                  {app.isFeatured ? (
                    <>
                      <span style={{
                        width: '5px',
                        height: '5px',
                        borderRadius: '50%',
                        background: 'var(--text-primary)',
                      }} />
                      <span style={{
                        fontSize: '8px',
                        color: 'var(--text-tertiary)',
                        textTransform: 'uppercase',
                      }}>FT</span>
                    </>
                  ) : (
                    <span style={{ fontSize: '8px', color: 'var(--text-disabled)' }}>—</span>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
            {searchTerm || filterFeatured ? 'No providers match your filters' : 'No app providers found'}
          </div>
        )}
      </div>

      {/* Info Footer */}
      <div style={{
        padding: '12px 24px',
        borderTop: '1px solid var(--border-subtle)',
        fontSize: '11px',
        color: 'var(--text-disabled)',
        background: 'var(--bg-subtle)',
      }}>
        App providers earn rewards by facilitating transactions on the Canton Network. Featured apps are registered with the DSO.
      </div>
    </section>
  );
}
